'use client';

import type { ReactNode } from 'react';
import { useState, useCallback, useMemo } from 'react';
import { BrainIcon, Cancel01Icon } from '@terra/ui-icons';
import { VStack, HStack } from '@terra/ui-styled-system';
import { Button } from '../../../../../components/styled/button';
import { Text } from '../../../../../components/styled/text';
import { IconButton } from '../../../../../components/icon-button';
import { useTranslations } from 'next-intl';
import type { EditContentProps } from '../../tool-config-types';
import type { MemoryData } from './types';
import { MemoryEditForm } from './memory-edit-form';

function MemoryEditContentWrapper({
  onClose,
  onSubmit,
  extractedData = [],
}: EditContentProps) {
  const t = useTranslations('designSystem.chat.editMemory');
  const initialMemory: MemoryData = useMemo(
    () =>
      extractedData.length > 0
        ? (extractedData[0] as MemoryData)
        : { content: '' },
    [extractedData],
  );
  const [memory, setMemory] = useState<MemoryData>(initialMemory);
  const handleSubmit = useCallback(() => {
    onSubmit({
      content: memory.content,
      ...(memory.category && { category: memory.category }),
    });
    onClose();
  }, [memory, onSubmit, onClose]);
  const isValid = memory.content.trim().length > 0;

  return (
    <VStack gap={3} padding={4} width="100%" h="full">
      <HStack
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        flexShrink={0}
      >
        <HStack gap={2} alignItems="center">
          <BrainIcon size={18} color="var(--colors-purple-9)" />
          <Text fontSize="md" fontWeight="bold">
            {t('title')}
          </Text>
        </HStack>
        <IconButton
          variant="ghost"
          size="xs"
          aria-label="Close"
          onClick={onClose}
        >
          <Cancel01Icon size={14} />
        </IconButton>
      </HStack>

      <MemoryEditForm memory={memory} onChange={setMemory} />

      <HStack justifyContent="flex-end" gap={2} width="100%" flexShrink={0}>
        <Button variant="outline" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button onClick={handleSubmit} disabled={!isValid}>
          {t('save')}
        </Button>
      </HStack>
    </VStack>
  );
}

export const renderMemoryEditContent = (props: EditContentProps): ReactNode => (
  <MemoryEditContentWrapper {...props} />
);
